import React, { forwardRef, useCallback, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { EditorState } from 'draft-js';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RephraseOptions } from 'screens/common/RephraseOptions/RephraseOptions';
import { RephraseOption } from 'screens/common/RephraseOptions/types';
import useHelpMeWriteTooltip from 'screens/common/useHelpMeWriteTooltip';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { CheckCircle, ThumbsDownGenAi, ThumbsUpGenAi } from 'assets/assetsV1/iconComponents';

import { SENTIMENTS } from 'constants/ProjectConstants';
import { checkEditorStateTextIsEqual } from 'constants/draftJSHelpers';
import { getQueryParams } from 'constants/helper';
import { sendRephrasedMeshMavenContentEvent } from 'constants/mixpanelHelper';
import { CF_GEN_AI_BUTTON_TYPES, sendCFGenAIButtonClickedEvent } from 'constants/mixpanelHelpers/cfGenAIEvents';

import { Button } from 'uiToolkitV1/Button';
import { CarouselControl } from 'uiToolkitV1/CarouselControl';
import { Divider } from 'uiToolkitV1/Divider';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import {
  useCustomerFeedbackLoading,
  useCustomerFeedbackMutation,
  useFeedbackAnalysisLoading,
  useRephraseTextLoading,
  useRephraseTextMutation,
} from '../../hooks/meshMavenDataHooks';
import { useFeedbackData, usePrimaryPanelAction } from '../../slice/feedbackSlice';
import FeedbackRTE from '../FeedbackRTE';
import MeshMavenLoading from './MeshMavenLoading';

export const mixpanelEventType = {
  shorten: 'Shorten',
  elaborate: 'Elaborate',
  fix_spelling_grammar: 'Fix grammar',
  improve_writing: 'Rephrase',
};

const RephraseEditor = forwardRef(({ rephraseLoadingText }: { rephraseLoadingText: string }, parentRef) => {
  const dispatch = useDispatch();

  const {
    feedbackSentimentType,
    revisionNumber,
    setRevisionNumber,
    revisions,
    selectedRevision,
    setRevisionByRevisionNumber,
    rephraseError,
    setRephraseError,
  } = useFeedbackData();
  const primaryPanelAction = usePrimaryPanelAction();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const updateEditorState = (editorState: EditorState) => {
    dispatch(
      setRevisionByRevisionNumber({
        revisionNumber,
        revision: {
          ...selectedRevision,
          updatedState: editorState,
          isReanalyzeEnabled: checkEditorStateTextIsEqual(editorState, selectedRevision.updatedState)
            ? selectedRevision.isReanalyzeEnabled
            : true,
        },
      })
    );
  };

  const onTabChange = (tabIndex: number) => {
    dispatch(setRevisionNumber({ revisionNumber: tabIndex }));
    dispatch(setRephraseError({ error: null }));
  };

  const isFetching = useRephraseTextLoading();
  const isAnalysisFetching = useFeedbackAnalysisLoading();

  if (isFetching) {
    return <MeshMavenLoading loadingText={rephraseLoadingText} />;
  }

  const placeholder =
    primaryPanelAction === 'SHARE'
      ? 'Type your feedback here (Use @ to tag people and # to include competencies)'
      : 'Type your feedback here (Use # to include competencies)';

  return (
    <Box p={3}>
      {rephraseError && (
        <TextLabel bold color="functional.C62C02" ml={3}>
          {rephraseError}
        </TextLabel>
      )}
      {/* @ts-ignore */}
      <FeedbackRTE
        placeholder={placeholder}
        parentRef={parentRef}
        editorState={selectedRevision.updatedState}
        updateEditorState={updateEditorState}
        mentionExclusions={feedbackSentimentType === SENTIMENTS.appreciative ? [loggedInEmployee.id] : []}
        hideMediaMenu
        useMentionFunctionality={primaryPanelAction === 'SHARE'}
      />
      {revisions.length > 1 && (
        <CarouselControl
          noOfTabs={revisions.length}
          selectedIndex={revisionNumber}
          onChange={onTabChange}
          isDisabled={isAnalysisFetching}
        />
      )}
    </Box>
  );
});

const CustomerFeedback = () => {
  const { selectedRevision } = useFeedbackData();
  const { mutate: submitCustomerFeedback } = useCustomerFeedbackMutation();

  const isFetching = useCustomerFeedbackLoading();
  const isRephraseFetching = useRephraseTextLoading();

  const handleFeedback = (action: 'LIKE' | 'DISLIKE') => {
    submitCustomerFeedback({ userAction: action });
  };

  if (isRephraseFetching || !selectedRevision.requestId) {
    return null;
  }

  if (selectedRevision.isAIFeedbackProvided) {
    return (
      <Flex alignItems="center" p={4}>
        <CheckCircle color="#44812F" />
        <TextBodyMedium color="#44812F" ml="4px">
          Thank you! Your response will help us improve Maven
        </TextBodyMedium>
      </Flex>
    );
  }

  return (
    <Flex
      borderTopWidth="1px"
      borderColor="neutral.EBEFF3"
      p={4}
      alignItems="center"
      bg="linear-gradient(91deg, rgba(0, 142, 240, 0.03) 1.81%, rgba(236, 59, 100, 0.03) 36.92%, rgba(255, 194, 0, 0.03) 69.52%, rgba(92, 196, 97, 0.03) 106.15%)"
    >
      <TextBodyMedium>Did you like my response?</TextBodyMedium>
      <Button
        isDisabled={isFetching}
        onClick={() => handleFeedback('LIKE')}
        StartIcon={ThumbsUpGenAi}
        variant="tertiary"
        ml="auto"
      />
      <Button
        isDisabled={isFetching}
        onClick={() => handleFeedback('DISLIKE')}
        StartIcon={ThumbsDownGenAi}
        variant="tertiary"
        ml={4}
      />
    </Flex>
  );
};

const MeshMavenRephrase = () => {
  const { mutate: rephraseText } = useRephraseTextMutation();
  const isRephraseFetching = useRephraseTextLoading();
  const isAnalysisFetching = useFeedbackAnalysisLoading();
  const parentRef = useRef(null);
  const history = useHistory();
  const [rephraseLoadingText, setRephraseLoadingText] = useState('');

  const { selectedRevision, revisionNumber } = useFeedbackData();
  const action = usePrimaryPanelAction();
  const helpMeWriteTooltip = useHelpMeWriteTooltip({
    editorState: selectedRevision.updatedState,
    checkMention: action === 'SHARE',
  });

  const { source } = getQueryParams(history.location.search);

  const handleRephraseText = useCallback(
    (rephraseOption: RephraseOption, loadingText: string) => {
      sendCFGenAIButtonClickedEvent({
        eventProperties: {
          source,
          type: CF_GEN_AI_BUTTON_TYPES.REPHRASE,
        },
      });
      sendRephrasedMeshMavenContentEvent({
        eventProperties: {
          source: 'Share Feedback',
          type: mixpanelEventType[rephraseOption],
        },
      });
      setRephraseLoadingText(loadingText.replace('moduleName', 'feedback'));
      rephraseText({ rephraseInstruction: rephraseOption });
    },
    [rephraseText, source]
  );

  if (!selectedRevision) {
    return null;
  }

  return (
    <Flex ref={parentRef} flexDir="column" borderRadius="8px" borderWidth="1px" borderColor="neutral.EBEFF3">
      <Flex p="10px 20px 6px" alignItems="center" justifyContent="space-between">
        <TextBodyMedium bold>Comment</TextBodyMedium>
        <RephraseOptions
          handleRephraseText={handleRephraseText}
          tooltipText={helpMeWriteTooltip.tooltip}
          isDisabled={isRephraseFetching || isAnalysisFetching || helpMeWriteTooltip.isDisabled}
          totalCharacters={helpMeWriteTooltip.textContentLength}
        />
      </Flex>
      <Divider color="neutral.EBEFF3" m={1} />
      <RephraseEditor key={revisionNumber} ref={parentRef} rephraseLoadingText={rephraseLoadingText} />
      <CustomerFeedback />
    </Flex>
  );
};

export default MeshMavenRephrase;
